import { BehaviorSubject } from 'rxjs';
import { Injectable } from "@angular/core";
import { NdmsSafeSubscriber } from "../../../../shared/core/interfaces/ndms-safe-subscriber";
import { RmsPolicyAuthorizationService } from "projects/rms/app/api/services/rms-policy-authorization.service";
import { PermissionsService } from 'projects/rms/app/api/services/permissions.service';
import { PermissionModel } from 'projects/rms/app/api/models/permission-model';

@Injectable()
export class RmsPolicyPermissionsService extends NdmsSafeSubscriber {
  private _effectivePermissions = new BehaviorSubject({ permissions: [], organizationId: undefined });
  get effectivePermissions$() {
    return this._effectivePermissions.asObservable();
  }
  get effectivePermissions() {
    return this._effectivePermissions.value || {permissions:[],organizationId:undefined};
  }

  private _missingPermissions$ = new BehaviorSubject({ permissions: [], organizationId: undefined });
  get missingPermissions$() {
    return this._missingPermissions$.asObservable();
  }
  get missingPermissions() {
    return this._missingPermissions$.value;
  }

  private _effectiveOrganizationPermissions = new BehaviorSubject({ permissions: [], organizationId: undefined });
  get effectiveOrganizationPermissions$() {
    return this._effectiveOrganizationPermissions.asObservable();
  }
  get effectiveOrganizationPermissions() {
    return this._effectiveOrganizationPermissions.value || {permissions:[],organizationId:undefined};
  }

  private _missingOrganizationPermissions$ = new BehaviorSubject({ permissions: [], organizationId: undefined });
  get missingOrganizationPermissions$() {
    return this._missingOrganizationPermissions$.asObservable();
  }
  get missingOrganizationPermissions() {
    return this._missingOrganizationPermissions$.value;
  }

  allPermissions: PermissionModel[] = [];

  applications: string[] = [
    'Admin',
    'RmsSystem',
    'Responders',
    'Teams',
    'Credentialing',
    'Pars',
    'SelfService',
    'Deployment',
    'Events',
    'Finance',
    'Reports',
    'Training',
    'Travel',
    'Development'
  ];

  static PermissionSets = {
    Core: [
      'Responder:Search:Access',
      'Team:Search:Access',
      'Credentialing:Access',
      'Admin:Access', 
      'Finance:Access', 
      'Event:Search:Access',
      'Roster:Search:Access',
      'Pars:Access',
      'Travel:TravelRoster:Search:Access',
      'Reports:Access',
      'SelfService:Access'
    ]
  };

  private loadEffectivePermissionsPromise: Promise<any>;

  constructor(
    private policyAuthorizationService: RmsPolicyAuthorizationService,
    private permissionsService: PermissionsService)
  {
    super();
  }

  hasAnyPermissions(permissionNames: string[], organizationId?: number) {
    return !!permissionNames.find((permissionName) => this.hasPermission(permissionName));
  }

  hasAllPermissions(permissionNames: string[], organizationId?: number) {
    let results = permissionNames.filter((permissionName) => this.hasPermission(permissionName));
    return results.length === permissionNames.length;
  }

  hasPermission(permissionName: string, organizationId?: number) {
    if (!permissionName) {
      return false;
    }

    if (organizationId) {
      if (organizationId != this.effectiveOrganizationPermissions.organizationId) {
        console.warn("Caution - permissions not loaded for this organization (id " + organizationId + "). Call loadEffectivePermissions for this organization to load permissions.");
        return false;
      }
      return !!this.effectiveOrganizationPermissions.permissions.find(i => {
        let invert = permissionName.startsWith('!');
        let matched = i.permissionName === permissionName.replace('!', '');
        return ((invert) ? !matched : matched) || i.permissionName === 'System:Root';
      });
    }

    return !!this.effectivePermissions.permissions.find(i => {
      let invert = permissionName.startsWith('!');
      let matched = i.permissionName === permissionName.replace('!', '');
      return ((invert) ? !matched : matched) || i.permissionName === 'System:Root';
    });
  }

  async loadEffectivePermissions(permissions: string[], organizationId?: number): Promise<any> {
    //org ID provided, skip over to the orgperm version
    if (organizationId) {
      return this.loadEffectiveOrganizationPermissions(permissions, organizationId);
    }

    //return early if all requested permissions are already cached
    let missingPermissions = permissions.filter((permission) => !!!this.effectivePermissions.permissions.find(i => i.permissionName === permission));
    if (missingPermissions.length === 0 && this.effectivePermissions.permissions.length > 0) {
      return Promise.resolve(this.effectivePermissions);
    }

    //return early if all missing permissions have been requested previously - no sense checking again
    let filteredMissingPermissions = missingPermissions.filter(permission => !!!this.missingPermissions.permissions.find(i => i === permission));
    if (filteredMissingPermissions && filteredMissingPermissions.length === 0) {
      return Promise.resolve(this.effectivePermissions);
    }

    //run the query
    let params: RmsPolicyAuthorizationService.EvaluateParams = {
      permissions: filteredMissingPermissions || [],
      organizationId: organizationId
    };
    let effectivePermissions = await this.policyAuthorizationService.Evaluate(params).toPromise();

    //housekeeping - cache permissions & missing permissions
    let currentEffectivePermissions = this.effectivePermissions;
    if (effectivePermissions) {
      currentEffectivePermissions.permissions = currentEffectivePermissions.permissions.concat(effectivePermissions);
      this._effectivePermissions.next(currentEffectivePermissions || { permissions: [], organizationId: null });
      missingPermissions = missingPermissions.filter(permission =>
        !!!currentEffectivePermissions.permissions.find(i => i.permissionName === permission));
      this._missingPermissions$.next({permissions: missingPermissions, organizationId: organizationId});
    }

    return currentEffectivePermissions;
  }

  async loadEffectiveOrganizationPermissions(permissions: string[], organizationId: number): Promise<any> {
    //drop previous permissions if from another org
    if (organizationId && organizationId !== this.effectiveOrganizationPermissions.organizationId) {
      this._effectiveOrganizationPermissions.next({ permissions: [], organizationId });
      this._missingPermissions$.next({ permissions: [], organizationId });
    }

    //return early if all requested permissions are already cached
    let missingOrganizationPermissions = permissions.filter((permission) => !!!this.effectiveOrganizationPermissions.permissions.find(i => i.permissionName === permission));
    if (missingOrganizationPermissions.length === 0 && this.effectiveOrganizationPermissions.permissions.length > 0) {
      return Promise.resolve(this.effectiveOrganizationPermissions);
    }

    //return early if all missing permissions have been requested previously - no sense checking again
    let filteredMissingOrganizationPermissions = missingOrganizationPermissions.filter(permission => !this.missingOrganizationPermissions.permissions.find(i => i === permission));
    if (filteredMissingOrganizationPermissions && filteredMissingOrganizationPermissions.length === 0) {
      return Promise.resolve(this.effectiveOrganizationPermissions);
    }

    //run the query
    let params: RmsPolicyAuthorizationService.EvaluateParams = {
      permissions: filteredMissingOrganizationPermissions || [],
      organizationId: organizationId
    };
    let effectiveOrganizationPermissions = await this.policyAuthorizationService.Evaluate(params).toPromise();

    //housekeeping - cache permissions & missing permissions
    let currentEffectiveOrganizationPermissions = this.effectiveOrganizationPermissions;
    if (effectiveOrganizationPermissions) {
      currentEffectiveOrganizationPermissions.permissions = currentEffectiveOrganizationPermissions.permissions.concat(effectiveOrganizationPermissions);
      this._effectiveOrganizationPermissions.next(currentEffectiveOrganizationPermissions || { permissions: [], organizationId: null });
      missingOrganizationPermissions = missingOrganizationPermissions.filter(permission =>
        !!!currentEffectiveOrganizationPermissions.permissions.find(i => i.permissionName === permission));
      this._missingPermissions$.next({ permissions: missingOrganizationPermissions, organizationId: organizationId });
    }

    return currentEffectiveOrganizationPermissions;
  }

  async loadPermissionsByApplication(application: string) : Promise<any> {
    let applicationPermissions = this.allPermissions.filter((permission) => 
      permission.applicationName.toLocaleLowerCase() === application.toLocaleLowerCase());
    if (applicationPermissions.length > 0) {
      return Promise.resolve(applicationPermissions);
    }
    let permissions = await this.permissionsService.GetPermissionsByApplication(application).toPromise();
    this.allPermissions = [...permissions];
    return this.allPermissions;
  }

  hasSystemRoot(effectivePermissions) {
    return !!effectivePermissions.permissions.find((permission) => permission.permissionName === 'System:Root');
  }
}
